import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import getMobilePlatform from 'helpers/mobilePlatform';
import { didRedirectToApp } from 'helpers/openInApp';
import AppDownloadSheet from './AppDownloadSheet';
import trackAppPrompt from './trackAppPrompt';

const DISMISSED_KEY = 'app_download_prompt_dismissed_at';
const SNOOZE_DAYS = 14;
const DELAY_MS = 3000;

// Só onde a pessoa está navegando. Checkout, painel, login, menu do
// restaurante e apps de garçom/estação ficam de fora: lá o sheet custa venda
// ou atrapalha trabalho.
const ALLOWED_PATHS = [
    /^\/$/,
    /^\/eventos(\/buscar)?\/?$/,
    /^\/estabelecimentos(\/buscar)?\/?$/,
    /^\/evento\//,
    /^\/estabelecimento\//,
    /^\/u\//,
    /^\/produtor(\/|$)/,
];

function isAllowedPath(pathname) {
    return ALLOWED_PATHS.some((pattern) => pattern.test(pathname));
}

function recentlyDismissed() {
    try {
        const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY));
        return Date.now() - dismissedAt < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    } catch {
        return false;
    }
}

function rememberDismissal() {
    try {
        localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
        // Sem storage o sheet volta na próxima visita; aceitável.
    }
}

// Não empilha com o CookieBanner: espera a pessoa responder a ele primeiro.
function cookiesDecided() {
    try {
        return localStorage.getItem('authorize_cookies') !== null;
    } catch {
        return true;
    }
}

/**
 * Sugere baixar o app a quem abre o site num celular ou tablet. Aparece depois
 * de 3s ou da primeira rolagem, uma vez por carga, e some por 14 dias ao fechar.
 */
export default function AppDownloadPrompt() {
    const { pathname } = useLocation();
    const [platform] = useState(getMobilePlatform);
    const [eligible] = useState(() => Boolean(platform) && !didRedirectToApp() && !recentlyDismissed());
    const [armed, setArmed] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const shownRef = useRef(false);

    const allowed = isAllowedPath(pathname);

    useEffect(() => {
        if (!eligible) return;

        const arm = () => setArmed(true);
        const timer = setTimeout(arm, DELAY_MS);
        window.addEventListener('scroll', arm, { passive: true, once: true });

        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', arm);
        };
    }, [eligible]);

    useEffect(() => {
        if (!eligible || !armed || !allowed || shownRef.current) return;

        function tryOpen() {
            if (!cookiesDecided()) return false;

            shownRef.current = true;
            setIsOpen(true);
            trackAppPrompt('app_prompt_shown', { store: platform });
            return true;
        }

        if (tryOpen()) return;

        const interval = setInterval(() => {
            if (tryOpen()) clearInterval(interval);
        }, 1000);

        return () => clearInterval(interval);
    }, [eligible, armed, allowed, platform]);

    // Foi para o checkout com o sheet aberto: some sem contar como dispensa.
    useEffect(() => {
        if (!allowed) setIsOpen(false);
    }, [allowed]);

    const close = useCallback(() => {
        rememberDismissal();
        setIsOpen(false);
    }, []);

    const dismiss = useCallback(() => {
        trackAppPrompt('app_prompt_dismissed', { store: platform });
        close();
    }, [platform, close]);

    function handleStoreClick(store) {
        trackAppPrompt('app_store_click', { store, source: 'prompt' });
        close();
    }

    return (
        <AppDownloadSheet
            isOpen={isOpen}
            platform={platform}
            onDismiss={dismiss}
            onStoreClick={handleStoreClick}
        />
    );
}
